/**
 * EDM Rich Text Editor — TipTap-based WYSIWYG for email body editing.
 *
 * Features:
 *   - Formatting toolbar (bold, italic, underline, headings, lists, alignment)
 *   - Image insertion: URL prompt, file upload, or browse library
 *   - Link insertion
 *   - Character/word count
 *   - Outputs HTML compatible with inline-CSS email rendering
 */

import React, { useEffect, useState, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import ImageExt from '@tiptap/extension-image';
import LinkExt from '@tiptap/extension-link';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import CharacterCount from '@tiptap/extension-character-count';
import {
    Bold,
    Italic,
    Underline as UnderlineIcon,
    Heading1,
    Heading2,
    List,
    ListOrdered,
    AlignLeft,
    AlignCenter,
    AlignRight,
    Image as ImageIcon,
    Link2,
    Unlink,
    Undo2,
    Redo2,
    Upload,
    FolderOpen,
} from 'lucide-react';
import { apiClient } from '../../services/api';
import EDMImageBrowser from './EDMImageBrowser';
// Bring the .edm-rich-* styles with the editor so it renders correctly
// when imported outside the EDM module (e.g. from EditableNewsletterViewer).
import './EDMModule.css';

interface Props {
    content: string;
    onChange: (html: string) => void;
    placeholder?: string;
    projectId?: string;
}

export interface EDMRichEditorHandle {
    insertHtmlAtCursor: (html: string) => void;   // parses as HTML (CTA buttons)
    insertTextAtCursor: (text: string) => void;   // inserts a text node, no HTML parsing (merge tags)
}

const EDMRichEditor = forwardRef<EDMRichEditorHandle, Props>(({ content, onChange, placeholder, projectId }, ref) => {
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [uploading, setUploading] = useState(false);
    const [browserOpen, setBrowserOpen] = useState(false);

    // Preserve inline style + target attributes on <a> tags so that
    // CTA buttons (which rely on inline background/color/padding) survive
    // a round-trip through the TipTap editor.
    const StyledLink = LinkExt.extend({
        addAttributes() {
            return {
                ...this.parent?.(),
                style: {
                    default: null,
                    parseHTML: (el: HTMLElement) => el.getAttribute('style'),
                    renderHTML: (attrs: any) => (attrs.style ? { style: attrs.style } : {}),
                },
                target: {
                    default: null,
                    parseHTML: (el: HTMLElement) => el.getAttribute('target'),
                    renderHTML: (attrs: any) => (attrs.target ? { target: attrs.target } : {}),
                },
            };
        },
    });

    const editor = useEditor({
        extensions: [
            // StarterKit v3 bundles Underline and Link; disable the bundled
            // versions here so the explicit Underline + StyledLink instances
            // below are the only ones registered (otherwise tiptap warns
            // about duplicate extension names and behavior is unpredictable).
            StarterKit.configure({
                heading: { levels: [1, 2, 3] },
                link: false,
                underline: false,
            } as any),
            Underline,
            TextAlign.configure({ types: ['heading', 'paragraph'] }),
            StyledLink.configure({ openOnClick: false }),
            ImageExt.configure({ inline: false, allowBase64: true }),
            Placeholder.configure({ placeholder: placeholder || 'Start writing your email...' }),
            CharacterCount,
        ],
        content: content || '',
        onUpdate: ({ editor }) => {
            onChange(editor.getHTML());
        },
    });

    useImperativeHandle(ref, () => ({
        insertHtmlAtCursor: (html: string) => {
            if (editor) {
                editor.chain().focus().insertContent(html).run();
            }
        },
        insertTextAtCursor: (text: string) => {
            if (editor) {
                // Text node, never the string form: a string is parsed as HTML,
                // which would turn `{{first_name|<b>x</b>}}` into <strong>.
                editor.chain().focus().insertContent({ type: 'text', text }).run();
            }
        },
    }), [editor]);

    // Sync external content changes (e.g., from AI generation or tab switch)
    useEffect(() => {
        if (editor && content !== editor.getHTML()) {
            editor.commands.setContent(content || '', { emitUpdate: false });
        }
    }, [content]);

    const handleInsertImageUrl = useCallback(() => {
        const url = window.prompt('Enter image URL:');
        if (url && editor) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    }, [editor]);

    const handleUploadImage = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file || !editor) return;

        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            if (projectId) formData.append('project_id', projectId);
            const res = await apiClient.post('/edm/upload-image', formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
            });
            editor.chain().focus().setImage({ src: res.data.url }).run();
        } catch {
            alert('Image upload failed');
        } finally {
            setUploading(false);
            e.target.value = '';
        }
    }, [editor, projectId]);

    const handleBrowseSelect = useCallback((url: string) => {
        if (editor) {
            editor.chain().focus().setImage({ src: url }).run();
        }
    }, [editor]);

    const handleInsertLink = useCallback(() => {
        if (!editor) return;
        const previousUrl = editor.getAttributes('link').href;
        const url = window.prompt('Enter URL:', previousUrl || 'https://');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        } else {
            editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
        }
    }, [editor]);

    if (!editor) return null;

    const chars = editor.storage.characterCount.characters();
    const words = editor.storage.characterCount.words();

    return (
        <div className="edm-rich-editor">
            <div className="edm-rich-toolbar">
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('bold') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleBold().run()} title="Bold">
                    <Bold size={15} />
                </button>
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('italic') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleItalic().run()} title="Italic">
                    <Italic size={15} />
                </button>
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('underline') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleUnderline().run()} title="Underline">
                    <UnderlineIcon size={15} />
                </button>
                <span className="edm-toolbar-divider" />
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('heading', { level: 1 }) ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} title="Heading 1">
                    <Heading1 size={15} />
                </button>
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('heading', { level: 2 }) ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} title="Heading 2">
                    <Heading2 size={15} />
                </button>
                <span className="edm-toolbar-divider" />
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('bulletList') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleBulletList().run()} title="Bullet List">
                    <List size={15} />
                </button>
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('orderedList') ? 'active' : ''}`} onClick={() => editor.chain().focus().toggleOrderedList().run()} title="Numbered List">
                    <ListOrdered size={15} />
                </button>
                <span className="edm-toolbar-divider" />
                <button type="button" className={`edm-toolbar-btn ${editor.isActive({ textAlign: 'left' }) ? 'active' : ''}`} onClick={() => editor.chain().focus().setTextAlign('left').run()} title="Align Left">
                    <AlignLeft size={15} />
                </button>
                <button type="button" className={`edm-toolbar-btn ${editor.isActive({ textAlign: 'center' }) ? 'active' : ''}`} onClick={() => editor.chain().focus().setTextAlign('center').run()} title="Align Center">
                    <AlignCenter size={15} />
                </button>
                <button type="button" className={`edm-toolbar-btn ${editor.isActive({ textAlign: 'right' }) ? 'active' : ''}`} onClick={() => editor.chain().focus().setTextAlign('right').run()} title="Align Right">
                    <AlignRight size={15} />
                </button>
                <span className="edm-toolbar-divider" />
                <button type="button" className={`edm-toolbar-btn ${editor.isActive('link') ? 'active' : ''}`} onClick={handleInsertLink} title="Insert Link">
                    <Link2 size={15} />
                </button>
                {editor.isActive('link') && (
                    <button type="button" className="edm-toolbar-btn" onClick={() => editor.chain().focus().unsetLink().run()} title="Remove Link">
                        <Unlink size={15} />
                    </button>
                )}
                <span className="edm-toolbar-divider" />
                <button type="button" className="edm-toolbar-btn" onClick={() => setBrowserOpen(true)} title="Browse Image Library">
                    <FolderOpen size={15} />
                </button>
                <button type="button" className="edm-toolbar-btn" onClick={handleInsertImageUrl} title="Insert Image URL">
                    <ImageIcon size={15} />
                </button>
                <button type="button" className={`edm-toolbar-btn ${uploading ? 'uploading' : ''}`} onClick={() => fileInputRef.current?.click()} title="Upload Image" disabled={uploading}>
                    <Upload size={15} />
                </button>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display: 'none' }} onChange={handleUploadImage} />
                <span className="edm-toolbar-divider" />
                <button type="button" className="edm-toolbar-btn" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
                    <Undo2 size={15} />
                </button>
                <button type="button" className="edm-toolbar-btn" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
                    <Redo2 size={15} />
                </button>
            </div>

            <EditorContent editor={editor} className="edm-rich-editor-content" />

            <div className="edm-rich-editor-footer">
                <span>{words} words</span>
                <span>{chars} characters</span>
            </div>

            <EDMImageBrowser
                open={browserOpen}
                onClose={() => setBrowserOpen(false)}
                onSelect={handleBrowseSelect}
                projectId={projectId}
            />
        </div>
    );
});
EDMRichEditor.displayName = 'EDMRichEditor';

export default EDMRichEditor;
